<template>
  <div>
    <template v-if="isVoiceRecording || (typingSubscribers && typingSubscribers.size > 0)">
      <div
        class="text-label-r-13-18 flex items-center min-w-0 font-normal pr-2"
        data-test="typing-indicator"
      >
        <template v-if="withNames && !isVoiceRecording && typingSubscribers">
          <span
            v-if="isCompactTyping && firstSubscriberName"
            class="text-ellipsis whitespace-nowrap overflow-hidden"
            data-test="typing-lead"
          >
            {{ firstSubscriberName }}&thinsp;{{ $t('chat.printingAndMore', { count: othersCount }) }}&thinsp;
          </span>
          <span
            v-else-if="isCompactTyping"
            class="shrink-0"
            data-test="typing-lead"
          >
            {{ typingSubscribers.size }}&thinsp;
          </span>
          <span
            v-else-if="subscriberNames"
            class="text-ellipsis whitespace-nowrap overflow-hidden"
            data-test="typing-lead"
          >
            {{ subscriberNames }}&thinsp;
          </span>
        </template>
        <p
          v-if="isVoiceRecording"
          class="shrink-0"
        >
          {{ $t('chat.recordingVoiceMessage') }}
          <span v-if="!withIcons">...</span>
        </p>
        <p
          v-else-if="typingSubscribers && typingSubscribers.size === 1"
          class="shrink-0"
          :class="{ capitalize: !withNames && !isGroup }"
          data-test="typing-verb"
        >
          {{ $tc('chat.printing', 1) }}
          <span v-if="!withIcons">...</span>
        </p>
        <p
          v-else
          class="shrink-0"
          data-test="typing-verb"
        >
          {{ $tc('chat.printing', 0) }}
          <span v-if="!withIcons">...</span>
        </p>
        <dots-loader
          v-if="withIcons"
          class="ml-2"
        />
      </div>
    </template>
    <template v-else>
      <slot />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'
import { useVoiceMessageStore } from '@/entities/voice-message'

import { DotsLoader } from '@/shared/components/loaders'
import { Room } from '@/shared/types/store'
import { fromPServedUser } from '@/shared/utils/uri'

import { TypingSubscribers, useTypingStore } from '../'

const props = withDefaults(defineProps<{
  pServed: string
  isGroup: boolean
  withIcons?: boolean
  withNames?: boolean
  maxNamedTypers?: number
}>(), {
  withNames: true,
  maxNamedTypers: 2,
})

const { getRoomById } = useStoreRooms()
const { typingsByPServed } = storeToRefs(useTypingStore())
const { remoteRecordingsByPServed } = storeToRefs(useVoiceMessageStore())
const { findByPServed } = useStoreContacts()

const id = computed<string>(() => {
  return props.isGroup ? fromPServedUser(props.pServed) : props.pServed
})

const room = computed<Room | undefined>(() => getRoomById(id.value))
const contacts = computed(() => room.value?.contacts)

const typingSubscribers = computed<TypingSubscribers | undefined>(() => {
  return typingsByPServed.value.get(props.pServed)
})

const isVoiceRecording = computed(() => {
  if (props.isGroup) return false
  return Boolean(remoteRecordingsByPServed.value.get(props.pServed)?.size)
})

const resolveNameBySubscriberId = (subscriberId: string): string | undefined => {
  const roomContact = contacts.value?.find((contact) => contact.id === subscriberId)
  if (roomContact?.name) return roomContact.name

  return findByPServed(subscriberId)?.name
}

const privateChatName = computed(() => {
  const contact = findByPServed(props.pServed)
  if (contact?.name) return contact.name
  return room.value?.name || ''
})

const resolvedSubscriberNames = computed<string[]>(() => {
  // Тет-а-тет: имя собеседника над footer / в списке чатов
  if (!props.isGroup) {
    return privateChatName.value ? [privateChatName.value] : []
  }

  if (!room.value) return []

  const names: string[] = []

  typingSubscribers.value?.forEach((_timestamp, subscriberId) => {
    if (!subscriberId) return
    const name = resolveNameBySubscriberId(subscriberId)
    if (name) names.push(name)
  })

  return names
})

const isCompactTyping = computed(() => {
  return props.isGroup && (typingSubscribers.value?.size ?? 0) > props.maxNamedTypers
})

const firstSubscriberName = computed(() => resolvedSubscriberNames.value[0] ?? '')

const othersCount = computed(() => {
  const size = typingSubscribers.value?.size ?? 0
  return Math.max(size - 1, 0)
})

const subscriberNames = computed(() => resolvedSubscriberNames.value.join(', '))
</script>
